const TIME = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const DAY = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long" });
/* То же с годом — для всего, что старше нынешнего года. */
const DAY_YEAR = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  year: "numeric",
});
/* «10 авг. 2024 г.» — даты в профиле, как у Discord: коротко и всегда с годом. */
const DAY_SHORT = new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "short", year: "numeric" });
/* «май 2020 г.» — когда важен не день, а давность: возраст сервера. */
const MONTH_YEAR = new Intl.DateTimeFormat("ru-RU", { month: "long", year: "numeric" });

const DAY_FULL = new Intl.DateTimeFormat("ru-RU", {
  day: "numeric",
  month: "long",
  hour: "2-digit",
  minute: "2-digit",
});

export function time(value: string | Date): string {
  return TIME.format(new Date(value));
}

/**
 * Дата без времени.
 *
 * Год приписывается, только если он не нынешний. В ленте это видно сразу:
 * канал с годовой историей листается вверх, и «17 августа» без года там
 * ничего не значит, — а у сегодняшних сообщений год был бы шумом.
 */
export function day(value: string | Date): string {
  const at = new Date(value);
  const now = new Date();

  return at.getFullYear() === now.getFullYear() ? DAY.format(at) : DAY_YEAR.format(at);
}

/** Дата всегда с годом — «24 октября 2026 г.», как разделитель дня в Discord. */
export function fullDay(value: string | Date): string {
  return DAY_YEAR.format(new Date(value));
}

export function dayShort(value: string | Date): string {
  return DAY_SHORT.format(new Date(value));
}

export function monthYear(value: string | Date): string {
  return MONTH_YEAR.format(new Date(value));
}

export function dayTime(value: string | Date): string {
  return DAY_FULL.format(new Date(value));
}

/**
 * Метка сообщения в шапке — как в Discord: сегодняшнее — одним временем,
 * вчерашнее — «Вчера, в 17:46», всё раньше — «03.10.2026, 00:34». Так дата
 * видна у самого сообщения, и плавающая плашка дня ленте не нужна.
 */
export function posted(value: string | Date, now = new Date()): string {
  const at = new Date(value);
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (sameDay(at, now)) return time(at);
  if (sameDay(at, yesterday)) return `Вчера, в ${time(at)}`;
  return `${STAMP.d.format(at)}, ${time(at)}`;
}

export function sameDay(a: string | Date, b: string | Date): boolean {
  const first = new Date(a);
  const second = new Date(b);
  return first.toDateString() === second.toDateString();
}

/*
 * Прошедшее время и склонение — платформой, а не своим правилом.
 *
 * Оба объекта заводятся один раз: `Intl` собирает под собой правила языка, и
 * создавать его на каждую строку ленты — заметная работа впустую.
 */
const RELATIVE = new Intl.RelativeTimeFormat("ru", { numeric: "auto" });
const PLURAL = new Intl.PluralRules("ru");

/**
 * «2 часа назад» — для списка сессий, где точное время не нужно.
 *
 * Ступени свои: «только что» и переход на дату `Intl` не знает — он про одну
 * величину в одной единице, а не про то, когда пора перестать считать часы.
 * Всё, что между ступенями, отдано ему: он же знает, что «вчера» и
 * «позавчера» говорят вместо чисел.
 */
export function ago(value: string | Date): string {
  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 90) return "только что";

  const minutes = Math.round(seconds / 60);
  if (minutes < 60) return RELATIVE.format(-minutes, "minute");

  const hours = Math.round(minutes / 60);
  if (hours < 24) return RELATIVE.format(-hours, "hour");

  const days = Math.round(hours / 24);
  if (days < 30) return RELATIVE.format(-days, "day");

  return day(value);
}

/**
 * Метка времени в сообщении — `<t:секунды:вид>`, как в Discord. Вид — буква
 * Discord: `t` «16:20», `T` «16:20:30», `d` «20.04.2021», `D` «20 апреля
 * 2021 г.», `f` (по умолчанию) — с временем, `F` — ещё и с днём недели, `R` —
 * «через 2 часа». Показывается в часовом поясе читающего: ради этого её и
 * пишут вместо «в восемь по Москве».
 */
export type StampStyle = "t" | "T" | "d" | "D" | "f" | "F" | "R";

const STAMP: Record<Exclude<StampStyle, "R">, Intl.DateTimeFormat> = {
  t: new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" }),
  T: new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }),
  d: new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" }),
  D: new Intl.DateTimeFormat("ru-RU", { day: "numeric", month: "long", year: "numeric" }),
  f: new Intl.DateTimeFormat("ru-RU", {
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }),
  F: new Intl.DateTimeFormat("ru-RU", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }),
};

export function stamp(at: Date, style: StampStyle = "f", now = Date.now()): string {
  return style === "R" ? relative(at, now) : STAMP[style].format(at);
}

/**
 * «через 2 часа» и «3 дня назад» — в обе стороны, в отличие от `ago`: метку
 * ставят и на будущее («сбор <t:…:R>»), и ступени здесь до лет, а не до даты.
 */
export function relative(at: Date, now = Date.now()): string {
  const seconds = Math.round((at.getTime() - now) / 1000);
  const steps: [number, Intl.RelativeTimeFormatUnit][] = [
    [60, "second"],
    [60, "minute"],
    [24, "hour"],
    [30, "day"],
    [12, "month"],
  ];
  let value = seconds;
  for (const [size, unit] of steps) {
    if (Math.abs(value) < size) return RELATIVE.format(value, unit);
    value = Math.round(value / size);
  }
  return RELATIVE.format(value, "year");
}

/** Сколько осталось: «4 мин 12 с». Для таймера на экране входа. */
export function countdown(until: Date): string {
  const left = Math.max(0, Math.round((until.getTime() - Date.now()) / 1000));
  if (left >= 60) {
    const minutes = Math.floor(left / 60);
    const seconds = left % 60;
    return `${minutes} ${plural(minutes, "минута", "минуты", "минут")} ${seconds} с`;
  }
  return `${left} ${plural(left, "секунда", "секунды", "секунд")}`;
}

/**
 * Форма слова при числе: «1 ответ», «2 ответа», «5 ответов».
 *
 * Правило считает не сам код, а `Intl.PluralRules`. Своё было в восемь строк и
 * помнило исключение второго десятка — но помнило его для одного языка,
 * а знает язык всё-таки платформа.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const form = PLURAL.select(count);
  return form === "one" ? one : form === "few" ? few : many;
}

export function fileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace(".", ",")} МБ`;
}

export function extension(filename: string, contentType: string): string {
  const dot = filename.lastIndexOf(".");
  if (dot > 0 && dot < filename.length - 1) {
    return filename.slice(dot + 1).slice(0, 4).toUpperCase();
  }
  const slash = contentType.indexOf("/");
  return slash > 0 ? contentType.slice(slash + 1).slice(0, 4).toUpperCase() : "ФАЙЛ";
}

/**
 * Место под картинку до загрузки.
 *
 * Размеры приходят вместе с вложением ровно для этого: без них лента
 * прыгает по мере подгрузки.
 */
export function imageBox(
  width: number | null,
  height: number | null,
  max = { w: 400, h: 300 },
): { width: number; height: number } {
  if (!width || !height) return { width: max.w, height: Math.round(max.w * 0.5625) };
  const scale = Math.min(1, max.w / width, max.h / height);
  return { width: Math.round(width * scale), height: Math.round(height * scale) };
}
